import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/soul";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Sparkles, Feather, HandHeart, Loader2 } from "lucide-react";

const INTERVALS = [
  { key: "weekly", label: "Weekly", suffix: "/week" },
  { key: "monthly", label: "Monthly", suffix: "/month" },
  { key: "yearly", label: "Yearly", suffix: "/year" },
];

const TIER_META = {
  free: {
    name: "Free", icon: Feather, tagline: "Come and see.",
    features: ["Daily Feed & Red Letters (KJV)", "Our Heart & Common Thread", "Browse The Well", "3 AI word lookups a day", "Breath Guide"],
  },
  seeker: {
    name: "Seeker", icon: Sparkles, tagline: "Walk the inner path.",
    features: ["Everything in Free", "Unlimited AI word lookups", "Truth Journal & Living Prompts", "Mood Pulse & Then vs. Now", "Save words, roots & streaks", "Speak It Aloud", "Save verses as shareable cards"],
  },
  contributor: {
    name: "Contributor", icon: HandHeart, tagline: "Help fill the well for others.",
    features: ["Everything in Seeker", "Share your own poems & photos to The Well", "A voice in our collective story", "Touch lives you may never meet"],
  },
};

export default function Pricing() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("monthly");
  const [loading, setLoading] = useState("");
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    api.get("/payments/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {});
  }, []);

  const priceOf = (tier) => plans.find((p) => p.tier === tier)?.prices?.[interval];
  const planOf = (tier) => plans.find((p) => p.tier === tier);

  // Yearly savings vs paying weekly for a whole year.
  const yearlySavingPct = (tier) => {
    const p = planOf(tier);
    if (!p) return 0;
    const weeklyYear = p.prices.weekly.amount * 52;
    const yearly = p.prices.yearly.amount;
    if (!weeklyYear) return 0;
    return Math.round((1 - yearly / weeklyYear) * 100);
  };

  const checkout = async (tier) => {
    if (!user) {
      toast.info("Please sign in to begin.");
      navigate("/login");
      return;
    }
    const price = priceOf(tier);
    if (!price) return;
    setLoading(tier);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: price.lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout.");
      setLoading("");
    }
  };

  const cancel = async () => {
    setCanceling(true);
    try {
      await api.post("/payments/cancel");
      toast.success("Your plan will end at the close of this billing period.");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not cancel.");
    } finally {
      setCanceling(false);
    }
  };

  const currentTier = user?.tier || "free";
  const isAdmin = user?.role === "admin";
  const sub = user?.subscription;

  return (
    <main data-testid="pricing-page" className="max-w-6xl mx-auto px-6 py-16">
      <div className="max-w-3xl mb-10 fade-up">
        <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-4">Walk together</p>
        <h1 className="serif text-4xl sm:text-6xl font-light text-[#F7F1E6]">
          Choose how you <span className="italic-serif text-[#D4AF37]">journey</span>
        </h1>
        <p className="mt-5 text-[#B4A897] leading-relaxed text-lg">
          Begin free. Go deeper as a Seeker. And if you feel called, become a Contributor —
          so that what you've learned and lived might quietly touch, or even save, a life you'll never meet.
        </p>
      </div>

      {sub?.status && sub.status !== null && !isAdmin && (
        <div data-testid="manage-subscription" className="glass rounded-2xl p-5 mb-10 flex flex-wrap items-center justify-between gap-4">
          <p className="text-[#F7F1E6]">
            You're on <span className="text-[#D4AF37] capitalize">{currentTier}</span>
            {sub.cancel_at_period_end ? " — ending at the period's close." : " — renewing automatically."}
          </p>
          {!sub.cancel_at_period_end && (
            <button data-testid="cancel-subscription-btn" onClick={cancel} disabled={canceling}
              className="rounded-full border border-white/15 px-5 py-2 text-sm text-[#B4A897] hover:text-[#C1272D] transition-colors duration-300">
              {canceling ? "Canceling…" : "Cancel subscription"}
            </button>
          )}
        </div>
      )}

      <div className="inline-flex items-center gap-1 rounded-full border border-white/10 p-1 mb-12" data-testid="interval-toggle">
        {INTERVALS.map((iv) => (
          <button key={iv.key} data-testid={`interval-${iv.key}`} onClick={() => setInterval(iv.key)}
            className={`relative text-sm px-5 py-2 rounded-full transition-colors duration-300 ${interval === iv.key ? "bg-[#D4AF37] text-[#17100C]" : "text-[#B4A897] hover:text-[#F7F1E6]"}`}>
            {iv.label}
            {iv.key === "yearly" && (
              <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold" style={{ color: interval === "yearly" ? "#17100C" : "#D4AF37" }}>
                Best value
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {["free", "seeker", "contributor"].map((tier) => {
          const meta = TIER_META[tier];
          const Icon = meta.icon;
          const price = priceOf(tier);
          const isCurrent = currentTier === tier;
          const featured = tier === "seeker";
          return (
            <div key={tier} data-testid={`plan-${tier}`}
              className={`glass rounded-3xl p-8 flex flex-col ${featured ? "border border-[#D4AF37]/50 gold-glow" : ""}`}>
              <div className="flex items-center gap-3">
                <span className="h-11 w-11 rounded-full bg-[#D4AF37]/10 grid place-items-center text-[#D4AF37]"><Icon size={20} /></span>
                <div>
                  <h2 className="serif text-2xl text-[#F7F1E6]">{meta.name}</h2>
                  <p className="text-xs text-[#B4A897]">{meta.tagline}</p>
                </div>
              </div>
              <div className="mt-6">
                {tier === "free" ? (
                  <p className="serif text-4xl text-[#F7F1E6]">Free</p>
                ) : (
                  <>
                    <p className="serif text-4xl text-[#F7F1E6]">
                      ${price ? price.amount.toFixed(2) : "—"}
                      <span className="text-base text-[#B4A897]">{INTERVALS.find((i) => i.key === interval).suffix}</span>
                    </p>
                    {interval === "yearly" && yearlySavingPct(tier) > 0 && (
                      <p data-testid={`yearly-saving-${tier}`} className="mt-2 inline-block rounded-full bg-[#D4AF37]/15 text-[#D4AF37] text-xs font-medium px-3 py-1">
                        Save {yearlySavingPct(tier)}% vs paying weekly
                      </p>
                    )}
                    {tier === "seeker" && planOf("seeker")?.trial_days > 0 && (
                      <p data-testid="seeker-trial-note" className="mt-2 text-sm text-[#D4AF37]">
                        {planOf("seeker").trial_days}-day free trial, then billed {interval}
                      </p>
                    )}
                  </>
                )}
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {meta.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[#B4A897]">
                    <Check size={17} className="text-[#D4AF37] shrink-0 mt-0.5" /> <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                {isCurrent ? (
                  <button data-testid={`plan-current-${tier}`} disabled
                    className="w-full rounded-full border border-[#D4AF37]/40 text-[#D4AF37] px-6 py-3 font-medium opacity-70">
                    Your plan
                  </button>
                ) : tier === "free" ? (
                  <button onClick={() => navigate("/")} className="w-full rounded-full border border-white/15 text-[#B4A897] px-6 py-3 hover:text-[#F7F1E6] transition-colors duration-300">
                    Keep exploring
                  </button>
                ) : (
                  <button data-testid={`plan-choose-${tier}`} onClick={() => checkout(tier)} disabled={loading === tier}
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-medium transition-colors duration-300 ${featured ? "bg-[#D4AF37] text-[#17100C] hover:bg-[#e6c860]" : "border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"}`}>
                    {loading === tier ? <Loader2 size={17} className="animate-spin" /> : null}
                    {loading === tier ? "Redirecting…" : (tier === "seeker" && meta && planOf("seeker")?.trial_days > 0 ? "Start 7-day free trial" : `Choose ${meta.name}`)}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-8 text-sm text-[#B4A897]">Auto-renews · cancel anytime · secure checkout by Stripe. Test card: 4242 4242 4242 4242.</p>
    </main>
  );
}
